import { useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Search, X, FileText, LayoutDashboard, Zap, BarChart3 } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useSearch } from "../SearchProvider";
import { searchIndex } from "../data";
import { filterSearchItems, resolveSearchLabel } from "../utils";
import { cn } from "@/shared/lib/cn";

type SearchBarProps = {
  className?: string;
};

export function SearchBar({ className }: SearchBarProps) {
  const { t } = useTranslation();
  const { query, setQuery, isOpen, open, close, inputRef } = useSearch();
  const containerRef = useRef<HTMLDivElement>(null);

  const results = filterSearchItems(searchIndex, query, t);

  useEffect(() => {
    if (!isOpen) return;

    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        close();
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isOpen, close]);

  return (
    <>
      {isOpen && (
        <button
          type="button"
          aria-label="Close search"
          className="fixed inset-0 z-40 bg-black/40 lg:hidden"
          onClick={close}
        />
      )}

      <div
        ref={containerRef}
        className={cn(
          "relative z-50 flex flex-1",
          isOpen && "fixed inset-x-3 top-3 sm:inset-x-4 sm:top-4 lg:static lg:inset-auto",
          className,
        )}
      >
        <div className="relative w-full">
          <Search
            size={18}
            className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-muted"
          />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => {
              setQuery(e.target.value);
              if (!isOpen) open();
            }}
            onFocus={open}
            placeholder={t.search.placeholder}
            className="h-11 w-full rounded-full border border-border/80 bg-card-muted pl-11 pr-16 text-sm text-foreground shadow-[var(--shadow-sm)] outline-none transition-colors placeholder:text-muted focus:border-primary focus:bg-card focus:ring-2 focus:ring-accent/30 sm:h-12 sm:pl-12 sm:pr-20"
          />
          <div className="absolute right-3 top-1/2 flex -translate-y-1/2 items-center gap-1">
            {query ? (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="flex h-5 w-5 items-center justify-center rounded text-muted hover:text-foreground"
                aria-label="Clear"
              >
                <X size={14} />
              </button>
            ) : (
              <kbd className="hidden rounded border border-border bg-card px-1.5 py-0.5 text-[10px] font-medium text-muted lg:inline">
                ⌘K
              </kbd>
            )}
          </div>
        </div>

        {isOpen && (
          <div className="theme-card theme-card-static absolute left-0 right-0 top-full z-50 mt-2 max-h-[400px] overflow-y-auto p-1">
            {results.length === 0 ? (
              <div className="px-4 py-8 text-center text-sm text-muted">
                {t.search.noResults}
              </div>
            ) : (
              <ul className="p-2">
                {results.map((item) => {
                  const Icon =
                    item.categoryKey === "search.indicators"
                      ? BarChart3
                      : item.categoryKey === "search.actions"
                        ? Zap
                        : item.type === "page"
                          ? LayoutDashboard
                          : FileText;
                  const category = resolveSearchLabel(t, item.categoryKey);

                  return (
                    <li key={item.id}>
                      <Link
                        to={item.href}
                        onClick={close}
                        className="flex items-center gap-3 rounded-xl px-3 py-2.5 transition-colors hover:bg-card-muted"
                      >
                        <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-primary-soft">
                          <Icon size={16} className="text-primary" />
                        </div>
                        <div className="min-w-0">
                          <p className="truncate text-sm font-medium text-foreground">
                            {resolveSearchLabel(t, item.labelKey)}
                          </p>
                          <p className="text-xs text-muted">{category}</p>
                        </div>
                      </Link>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    </>
  );
}
